import React, { useState } from "react";
import { motion } from "framer-motion";
import PhoneInput from "react-phone-input-2";
import {
  ArrowLeftOutlined,
  EditOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Alert, Tooltip } from "antd";
import { useResponsive } from "../../hooks/useResponsive";
import { Link } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import useUser from "../../hooks/useUser";
import Spinner from "../Spinner";
import { userState } from "../../store/slices/loginSlice";
import { getUser } from "../../hooks/useSignup";
import useGlobalHandler from "../../hooks/useGlobalHandler";
import store from "../../store/store";
import { useTranslation } from "react-i18next";

const PersonalInfo = () => {
  const { t } = useTranslation("postAuth", {
    keyPrefix: "profile",
  });
  const { mobileOnly } = useResponsive();
  const [edit, setEdit] = useState(false);
  const user = store.getState().login.user;

  const updatedInfoSchema = Yup.object().shape({
    displayName: Yup.string().min(2, "Too Short!").max(50, "Too Long!"),

    email: Yup.string().email("Invalid email"),
    sex: Yup.mixed().oneOf(["male", "female"]),
    birthdate: Yup.date().max(new Date(), "You can't be born in the future!"),
    paymentAddress: Yup.string().min(2, "Too Short!").max(50, "Too Long!"),
  });

  const { isLoading, setIsLoading, isError, setIsError, error, setError } =
    useGlobalHandler();

  const { updateUserProfile } = useUser();

  return (
    <>
      <Formik
        initialValues={{
          sex: user?.sex,
          birthdate: user?.birthdate,
          displayName: user?.displayName,
          email: user?.email || "",
          paymentAddress: user?.paymentAddress,
          uid: user?.uid,
        }}
        validationSchema={updatedInfoSchema}
        onSubmit={async (values) => {
          const { uid } = values;
          try {
            setIsError(false);
            setError("");
            setIsLoading(true);
            await updateUserProfile(values);
            const logedInUser = await getUser(uid);
            store.dispatch(userState({ ...logedInUser }));
            setEdit(false);
            setIsLoading(false);
          } catch (error) {
            setIsError(true);
            setError(error.message);
          }
        }}
      >
        {({ errors, touched, values, setFieldValue, field }) => (
          <div className="bg-white py-4 px-8 rounded-lg m-4 mediumShadow">
            {mobileOnly && (
              <span className="flex  justify-between">
                <motion.div whileTap={{ scale: 0.8 }} className="mb-8">
                  <Link to="/profile">
                    {" "}
                    <ArrowLeftOutlined />{" "}
                    <span className="font-semibold text-lg">Back</span>
                  </Link>
                </motion.div>
                <motion.div
                  whileTap={{ scale: 0.8 }}
                  className="mb-8 cursor-pointer"
                  onClick={() => setEdit(!edit)}
                >
                  Edit <EditOutlined />
                </motion.div>
              </span>
            )}
            <Form className="flex flex-col gap-8   ">
              {isLoading ? (
                <Spinner className="flex justify-center items-center h-screen w-full" />
              ) : (
                <>
                  <div className="flex flex-col gap-4">
                    <span className="flex justify-between items-center">
                      <h1 className="LoginTtitle">
                        Personal Info{" "}
                        <Tooltip placement="topRight" title={" معلومات شخصية"}>
                          <InfoCircleOutlined />
                        </Tooltip>{" "}
                      </h1>
                      {!mobileOnly && (
                        <motion.div
                          whileTap={{ scale: 0.8 }}
                          className="cursor-pointer"
                          onClick={() => setEdit(!edit)}
                        >
                          Edit <EditOutlined />
                        </motion.div>
                      )}
                    </span>
                    <div className="flex flex-col md:flex-row items-start md:items-center w-full gap-4 flex-wrap">
                      <span className="w-full flex  flex-col md:flex-row gap-2 items-start md:items-center relative whitespace-nowrap">
                        <label className="text-primaryColor">
                          Full Name :{" "}
                          <Tooltip placement="topRight" title={"الاسم الكامل"}>
                            <InfoCircleOutlined />
                          </Tooltip>{" "}
                        </label>
                        <Field
                          name="displayName"
                          id="displayName"
                          type="text"
                          className="border-2 rounded-md p-1 flex-1 w-full "
                          placeholder={values.displayName || user?.displayName}
                          disabled={!edit}
                        />
                      </span>
                      <span className="w-full flex flex-col md:flex-row  gap-2 items-start md:items-center whitespace-nowrap">
                        <label className="text-primaryColor ">
                          {t("sex")} :{" "}
                          <Tooltip placement="topRight" title={"الجنس"}>
                            <InfoCircleOutlined />
                          </Tooltip>{" "}
                        </label>
                        <Field
                          name="sex"
                          id="sex"
                          as="select"
                          className="border-2 rounded-md p-1 flex-1 w-full"
                          disabled={!edit}
                          value={values?.sex || user?.sex}
                        >
                          <option value="">{t("select your sex")}</option>
                          <option value="male">{t("male")}</option>
                          <option value="female">{t("female")}</option>
                        </Field>
                      </span>
                    </div>
                    <span className="w-full flex flex-col md:flex-row gap-2 items-start md:items-center whitespace-nowrap">
                      <label className="text-primaryColor inline-block">
                        {t("date of birth")} :{" "}
                        <Tooltip placement="topRight" title={"تاريخ الميلاد"}>
                          <InfoCircleOutlined />
                        </Tooltip>{" "}
                      </label>
                      <Field
                        className="w-full"
                        disabled={!edit}
                        name="birthdate"
                        type="date"
                        value={values?.birthdate || user?.birthdate}
                      />
                    </span>
                  </div>
                  <div className="flex flex-col gap-4 ">
                    <h1 className="LoginTtitle">
                      Contact Info{" "}
                      <Tooltip placement="topRight" title={"بيانات الإتصال"}>
                        <InfoCircleOutlined />
                      </Tooltip>{" "}
                    </h1>
                    <div className="flex flex-col md:flex-row items-start md:items-center w-full gap-8 flex-wrap">
                      <span className="w-full flex gap-2 flex-col md:flex-row items-start md:items-center whitespace-nowrap">
                        <label className="text-primaryColor inline-block ">
                          Phone Number :{" "}
                          <Tooltip placement="topRight" title={"رقم الهاتف "}>
                            <InfoCircleOutlined />
                          </Tooltip>{" "}
                        </label>
                        <PhoneInput
                          inputStyle={{
                            width: `100%`,
                            borderStyle: "solid",
                            borderColor: "#84CC16",
                          }}
                          name="phoneNumber"
                          id="phoneNumber"
                          disabled={true}
                          value={user?.phoneNumber}
                          country={"dz"}
                          {...field}
                          onChange={(value) =>
                            setFieldValue("phoneNumber", `+${value}`)
                          }
                        />

                        <Alert message="Verified" type="success" showIcon />
                      </span>
                      <span className="w-full flex flex-col md:flex-row items-start md:items-center gap-2  whitespace-nowrap ">
                        <label className="text-primaryColor">
                          Email :{" "}
                          <Tooltip
                            placement="topRight"
                            title={" البريد الإلكتروني"}
                          >
                            <InfoCircleOutlined />
                          </Tooltip>{" "}
                        </label>
                        <Field
                          className={`border-2 rounded-md p-1 flex-1 w-full ${
                            user?.emailVerified
                              ? "border-lime-500"
                              : "border-yellow-600"
                          }`}
                          name="email"
                          placeholder="Email"
                          disabled={true}
                          value={values?.email || user?.email}
                        />

                        {user?.emailVerified ? (
                          <Alert message="Verified" type="success" showIcon />
                        ) : (
                          <motion.span
                            whileTap={{ scale: 0.9 }}
                            className="cursor-pointer"
                          >
                            <Alert
                              message="Unverified"
                              type="warning"
                              showIcon
                            />
                          </motion.span>
                        )}
                      </span>
                      <span className="w-full flex flex-col md:flex-row gap-2 items-start md:items-center  whitespace-nowrap">
                        <label className="text-primaryColor">
                          {t("address")} :{" "}
                          <Tooltip
                            placement="topRight"
                            title={"العنوان الشخصي"}
                          >
                            <InfoCircleOutlined />
                          </Tooltip>{" "}
                        </label>

                        <Field
                          name="paymentAddress"
                          id="paymentAddress"
                          type="text"
                          className="border-2 rounded-md p-1 flex-1 w-full"
                          placeholder={
                            values?.paymentAddress ||
                            user?.paymentAddress ||
                            t("address")
                          }
                          value={values?.paymentAddress || user?.paymentAddress}
                          disabled={!edit}
                        />
                      </span>
                    </div>
                  </div>
                  {isError && <Alert message={error} type="error" showIcon />}
                  {errors.displayName && touched.displayName ? (
                    <Alert message={errors.displayName} type="error" showIcon />
                  ) : null}
                  {errors.sex && touched.sex ? (
                    <Alert message={errors.sex} type="error" showIcon />
                  ) : null}{" "}
                  {errors.birthdate && <touched className="birth"></touched> ? (
                    <Alert message={errors.birthdate} type="error" showIcon />
                  ) : null}{" "}
                  {errors.paymentAddress && touched.paymentAddress ? (
                    <Alert
                      message={errors.paymentAddress}
                      type="error"
                      showIcon
                    />
                  ) : null}
                  <div className="flex flex-col gap-1 mx-4">
                    <motion.button
                      type="submit"
                      whileTap={edit ? { scale: 0.9 } : { scale: 1 }}
                      whileHover={edit ? { scale: 1.05 } : { scale: 1 }}
                      className="primaryLoginButton w-auto self-center disabled:bg-primaryColor/50 "
                      disabled={!edit}
                    >
                      Confirm
                    </motion.button>
                  </div>
                </>
              )}
            </Form>{" "}
          </div>
        )}
      </Formik>
    </>
  );
};

export default PersonalInfo;
